import { useState, useEffect } from 'react';
import { supabase, safeRefreshSession } from './supabase';
import { supabaseData } from './supabaseData';
import {
  FileText, Briefcase, Search, Plus, Calendar, DollarSign,
  Clock, CheckCircle, Edit2, Trash2, User, MapPin, X, Save,
  Filter, ArrowUpDown, ChevronDown, CreditCard, Eye, Printer, Download, Send, Link, Receipt,
  Mail, MessageSquare, Copy, Edit3, UserPlus, ClipboardList
} from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';
import JobPhotos from './JobPhotos';
import JobCompletionModal from './JobCompletionModal';
import JobPlannerModal from './JobPlannerModal';
import InvoicingModule from './invoicing-module';
import CustomerCommunications from './CustomerCommunications';
import jsPDF from 'jspdf';
import { formatDisplayDate } from './dateUtils';
import { isOfflineModeEnabled, getCachedData, cacheData } from './offlineCache';
import AuditLogViewer from './AuditLogViewer';

export default function WorkOrders({ companyId, onNavigate, initialMode = 'list', initialTab = 'quotes', initialQuoteId, initialJobId, initialInvoiceId, isMobile, textingEnabled, userRole }) {
  const { colors } = useTheme();
  const { showConfirm } = useModal();
  const isFieldTech = userRole === 'field_tech';
  const viewableTabs = isFieldTech ? ['jobs'] : ['quotes', 'jobs', 'invoices'];
  const safeInitialTab = viewableTabs.includes(initialTab) ? initialTab : 'jobs';
  const [viewMode, setViewMode] = useState(initialMode); // 'list', 'new-quote', 'new-job'
  const [activeTab, setActiveTab] = useState(safeInitialTab); // quotes, jobs, or invoices
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  // Status filters for each tab
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('all'); // 'all', 'draft', 'sent', 'signed'
  const [jobStatusFilter, setJobStatusFilter] = useState('all'); // 'all', 'unscheduled', 'scheduled', 'in-progress', 'completed'

  // Sorting
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'value-high', 'value-low', 'name'

  // Edit/View modals
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [editingJobItems, setEditingJobItems] = useState([]);
  const [loadingJobItems, setLoadingJobItems] = useState(false);
  const [showAddJobItem, setShowAddJobItem] = useState(false);
  const [newJobItemName, setNewJobItemName] = useState('');
  const [newJobItemPrice, setNewJobItemPrice] = useState('');
  const [newJobItemQty, setNewJobItemQty] = useState(1);
  const [products, setProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // Quote items editing
  const [editingQuoteItems, setEditingQuoteItems] = useState([]);
  const [loadingQuoteItems, setLoadingQuoteItems] = useState(false);
  const [showAddQuoteItem, setShowAddQuoteItem] = useState(false);
  const [newQuoteItemName, setNewQuoteItemName] = useState('');
  const [newQuoteItemPrice, setNewQuoteItemPrice] = useState('');
  const [newQuoteItemQty, setNewQuoteItemQty] = useState(1);
  const [showQuoteProductDropdown, setShowQuoteProductDropdown] = useState(false);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [jobToSchedule, setJobToSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('9:00');
  const [pdfLoading, setPdfLoading] = useState(null); // quote/invoice id currently being generated

  // Job completion modal
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [jobToComplete, setJobToComplete] = useState(null);

  // Job planner modal
  const [showPlannerModal, setShowPlannerModal] = useState(false);
  const [jobToPlan, setJobToPlan] = useState(null);

  // Invoice view modal
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // Create job from quote modal
  const [quoteToCreateJob, setQuoteToCreateJob] = useState(null);
  const [creatingJob, setCreatingJob] = useState(false);

  // View signature modal
  const [viewingSignature, setViewingSignature] = useState(null);

  // Communications modal
  const [showCommunicationsModal, setShowCommunicationsModal] = useState(false);
  const [selectedCustomerForComms, setSelectedCustomerForComms] = useState(null);

  // Send Quote modal
  const [showSendQuoteModal, setShowSendQuoteModal] = useState(false);
  const [quoteToSend, setQuoteToSend] = useState(null);
  const [sendQuoteMethod, setSendQuoteMethod] = useState('email');
  const [sendQuoteEmail, setSendQuoteEmail] = useState('');
  const [sendQuotePhone, setSendQuotePhone] = useState('');
  const [editingSendContactInfo, setEditingSendContactInfo] = useState(false);
  const [sendQuoteAdditionalRecipients, setSendQuoteAdditionalRecipients] = useState([]);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [quoteLinkCopied, setQuoteLinkCopied] = useState(false);

  // Follow-up job from visit
  const [visitToScheduleFollowUp, setVisitToScheduleFollowUp] = useState(null);
  const [creatingFollowUp, setCreatingFollowUp] = useState(false);

  // Quote form state
  const [quoteForm, setQuoteForm] = useState({
    customer_id: '',
    quote_number: '',
    notes: '',
    items: []
  });

  // Job form state  
  const [jobForm, setJobForm] = useState({
    customer_id: '',
    job_number: '',
    job_type: '',
    scheduled_date: '',
    scheduled_time: '',
    notes: ''
  });


  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingJob) { setEditingJob(null); setEditingJobItems([]); }
        else if (showCompletionModal) { setShowCompletionModal(false); setJobToComplete(null); }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [editingJob, showCompletionModal]);

  useEffect(() => {
    if (companyId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    filterData();
  }, [activeTab, quotes, jobs, searchTerm, quoteStatusFilter, jobStatusFilter, sortBy]);

  // Open specific quote/job from notification deep link
  useEffect(() => {
    if (!quotes.length && !jobs.length) return;
    if (initialQuoteId) {
      const quote = quotes.find(q => q.id === initialQuoteId);
      if (quote) {
        setActiveTab('quotes');
        openEditQuoteModal(quote);
      }
    } else if (initialJobId) {
      const job = jobs.find(j => j.id === initialJobId);
      if (job) {
        setActiveTab('jobs');
        setEditingJob(job);
      }
    }
  }, [initialQuoteId, initialJobId, quotes.length, jobs.length]);

  const loadData = async (retryCount = 0) => {
    try {
      // Offline: serve from cache
      if (!navigator.onLine && isOfflineModeEnabled()) {
        const cachedCustomers = getCachedData('customers', companyId);
        const cachedQuotes = getCachedData('quotes', companyId);
        const cachedJobs = getCachedData('jobs', companyId);
        const cachedSkus = getCachedData('skus', companyId);
        if (cachedCustomers || cachedQuotes || cachedJobs || cachedSkus) {
          setCustomers(cachedCustomers || []);
          setQuotes(cachedQuotes || []);
          setJobs(cachedJobs || []);
          setProducts(cachedSkus || []);
          setLoading(false);
          return;
        }
      }

      setLoading(true);

      // Load customers
      const customersResult = await supabaseData
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .order('last_name', { ascending: true });

      // Load quotes with customer info
      const quotesResult = await supabaseData
        .from('quotes')
        .select(`
          *,
          customers (
            id,
            first_name,
            last_name,
            name,
            phone,
            email,
            address,
            city,
            state,
            zip
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      // Load jobs with customer info
      const jobsResult = await supabaseData
        .from('jobs')
        .select(`
          *,
          customers (
            id,
            first_name,
            last_name,
            name,
            phone,
            email,
            address,
            city,
            state,
            zip
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      // Load SKUs/products for dropdowns
      const productsResult = await supabaseData
        .from('skus')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      // Check for errors in any query
      const queryError = customersResult.error || quotesResult.error || jobsResult.error || productsResult.error;
      if (queryError) {
        if (retryCount < 1) {
          console.warn('WorkOrders: query error, retrying with session refresh...', queryError.message);
          await safeRefreshSession();
          await new Promise(r => setTimeout(r, 1000));
          return loadData(retryCount + 1);
        }
        throw queryError;
      }

      setCustomers(customersResult.data || []);
      setQuotes(quotesResult.data || []);
      setJobs(jobsResult.data || []);
      setProducts(productsResult.data || []);

      // Cache on success
      if (isOfflineModeEnabled()) {
        cacheData('customers', companyId, customersResult.data || []);
        cacheData('quotes', companyId, quotesResult.data || []);
        cacheData('jobs', companyId, jobsResult.data || []);
        cacheData('skus', companyId, productsResult.data || []);
      }

      // Check if we should pre-fill a job for a customer (from "Create Job" button on customer)
      const customerData = localStorage.getItem('createJobForCustomer');
      if (customerData) {
        try {
          const customer = JSON.parse(customerData);
          setJobForm({
            customer_id: customer.customer_id,
            job_number: '',
            job_type: '',
            scheduled_date: '',
            scheduled_time: '',
            notes: ''
          });
          setActiveTab('jobs');
          setViewMode('new-job');
          localStorage.removeItem('createJobForCustomer');
        } catch (e) {
          // Ignore parse errors
        }
      }
    } catch (error) {
      // Fallback to cache
      if (isOfflineModeEnabled()) {
        const cachedCustomers = getCachedData('customers', companyId);
        const cachedQuotes = getCachedData('quotes', companyId);
        const cachedJobs = getCachedData('jobs', companyId);
        const cachedSkus = getCachedData('skus', companyId);
        if (cachedCustomers || cachedQuotes || cachedJobs || cachedSkus) {
          setCustomers(cachedCustomers || []);
          setQuotes(cachedQuotes || []);
          setJobs(cachedJobs || []);
          setProducts(cachedSkus || []);
          setLoading(false);
          return;
        }
      }

      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let data = activeTab === 'quotes' ? [...quotes] : [...jobs];

    // Apply status filter
    if (activeTab === 'quotes' && quoteStatusFilter !== 'all') {
      data = data.filter(item => item.status === quoteStatusFilter);
    } else if (activeTab === 'jobs' && jobStatusFilter !== 'all') {
      if (jobStatusFilter === 'unscheduled') {
        data = data.filter(item => !item.scheduled_date);
      } else {
        data = data.filter(item => item.status === jobStatusFilter);
      }
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(item => {
        const customerName = getCustomerName(item.customers);
        const number = activeTab === 'quotes' ? item.quote_number : item.job_number;

        return (
          customerName.toLowerCase().includes(term) ||
          number?.toLowerCase().includes(term) ||
          item.customers?.phone?.toLowerCase().includes(term) ||
          item.customers?.email?.toLowerCase().includes(term)
        );
      });
    }

    // Apply sorting
    data.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'value-high':
          const aVal = activeTab === 'quotes' ? (a.total || 0) : (a.quoted_value || 0);
          const bVal = activeTab === 'quotes' ? (b.total || 0) : (b.quoted_value || 0);
          return bVal - aVal;
        case 'value-low':
          const aValLow = activeTab === 'quotes' ? (a.total || 0) : (a.quoted_value || 0);
          const bValLow = activeTab === 'quotes' ? (b.total || 0) : (b.quoted_value || 0);
          return aValLow - bValLow;
        case 'name':
          const aName = getCustomerName(a.customers);
          const bName = getCustomerName(b.customers);
          return aName.localeCompare(bName);
        default:
          return 0;
      }
    });

    setFilteredData(data);
  };

  // Count helpers for status badges
  const getQuoteStatusCounts = () => ({
    all: quotes.length,
    draft: quotes.filter(q => q.status === 'draft').length,
    sent: quotes.filter(q => q.status === 'sent').length,
    signed: quotes.filter(q => q.status === 'signed').length
  });

  const getJobStatusCounts = () => ({
    all: jobs.length,
    unscheduled: jobs.filter(j => !j.scheduled_date).length,
    scheduled: jobs.filter(j => j.status === 'scheduled' && j.scheduled_date).length,
    'in-progress': jobs.filter(j => j.status === 'in-progress').length,
    completed: jobs.filter(j => j.status === 'completed').length
  });

  const getCustomerName = (customer) => {
    if (!customer) return 'Unknown Customer';
    if (customer.first_name && customer.last_name) {
      return `${customer.last_name}, ${customer.first_name}`;
    }
    return customer.name || customer.first_name || customer.last_name || 'Unknown';
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: colors.textMuted,
      sent: colors.info,
      signed: colors.success,
      rejected: colors.error,
      scheduled: colors.info,
      'in-progress': colors.warning,
      completed: colors.success,
      cancelled: colors.error
    };
    return statusColors[status] || colors.textMuted;
  };

  const generateQuoteNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `Q-${year}${month}-${random}`;
  };

  const generateJobNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `J-${year}${month}-${random}`;
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { data, error } = await supabaseData
        .from('quotes')
        .insert([{
          company_id: companyId,
          customer_id: quoteForm.customer_id,
          quote_number: generateQuoteNumber(),
          status: 'draft',
          notes: quoteForm.notes
        }])
        .select()
        .single();

      if (error) throw error;

      alert('✅ Quote created successfully!');
      setViewMode('list');
      setQuoteForm({ customer_id: '', quote_number: '', notes: '', items: [] });
      loadData();
    } catch (error) {
      alert('Failed to create quote: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { data, error } = await supabaseData
        .from('jobs')
        .insert([{
          company_id: companyId,
          customer_id: jobForm.customer_id,
          job_number: generateJobNumber(),
          job_type: jobForm.job_type,
          status: 'scheduled',
          scheduled_date: jobForm.scheduled_date || null,
          scheduled_time: jobForm.scheduled_time || null,
          notes: jobForm.notes
        }])
        .select()
        .single();

      if (error) throw error;

      alert('✅ Job created successfully!');
      setViewMode('list');
      setJobForm({ customer_id: '', job_number: '', job_type: '', scheduled_date: '', scheduled_time: '', notes: '' });
      loadData();
    } catch (error) {
      alert('Failed to create job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item, type) => {
    const name = type === 'quote' ? item.quote_number : item.job_number;
    if (!window.confirm(`Delete ${name}?`)) return;

    try {
      const table = type === 'quote' ? 'quotes' : 'jobs';
      const { error } = await supabaseData
        .from(table)
        .delete()
        .eq('id', item.id);

      if (error) throw error;
      loadData();
    } catch (error) {
      alert('Error deleting: ' + error.message);
    }
  };

  // Open Send Quote modal
  const openSendQuoteModal = (quote) => {
    const customer = quote.customers;
    setQuoteToSend(quote);
    setSendQuoteEmail(customer?.email || '');
    setSendQuotePhone(customer?.phone || '');
    setEditingSendContactInfo(false);
    setSendQuoteAdditionalRecipients([]);
    setSendQuoteMethod('email');
    setQuoteLinkCopied(false);
    setShowSendQuoteModal(true);
  };

  // Get quote URL for remote signing
  const getQuoteUrl = (quoteId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/quote/${quoteId}`;
  };

  // Copy quote link to clipboard
  const copyQuoteLink = async () => {
    if (!quoteToSend) return;
    try {
      await navigator.clipboard.writeText(getQuoteUrl(quoteToSend.id));
      setQuoteLinkCopied(true);
      setTimeout(() => setQuoteLinkCopied(false), 3000);
    } catch (err) {
    }
  };

  // Handle sending the quote
  const handleSendQuote = async () => {
    if (!quoteToSend?.id) return;

    const customer = quoteToSend.customers;
    if (!customer) {
      alert('Customer not found');
      return;
    }

    const primaryEmail = sendQuoteEmail?.trim();
    const primaryPhone = sendQuotePhone?.trim();
    const quoteUrl = getQuoteUrl(quoteToSend.id);

    // Build list of all recipients
    const allEmails = [primaryEmail, ...sendQuoteAdditionalRecipients.map(r => r.email?.trim())].filter(Boolean);
    const allPhones = [primaryPhone, ...sendQuoteAdditionalRecipients.map(r => r.phone?.trim())].filter(Boolean);

    setSendingQuote(true);
    try {
      let emailsSent = 0;
      let smsSent = 0;

      // Update customer record if contact info changed
      if (primaryEmail !== customer.email || primaryPhone !== customer.phone) {
        const updateData = {};
        if (primaryEmail && primaryEmail !== customer.email) updateData.email = primaryEmail;
        if (primaryPhone && primaryPhone !== customer.phone) updateData.phone = primaryPhone;
        if (Object.keys(updateData).length > 0) {
          await supabaseData.from('customers').update(updateData).eq('id', customer.id);
        }
      }

      // Send emails
      if (sendQuoteMethod === 'email' || sendQuoteMethod === 'both') {
        for (const email of allEmails) {
          const emailResponse = await fetch('/api/email-send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: email,
              subject: `You have a quote ready for review`,
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #1a1a1a;">You have a new quote ready for review</h2>
                  <p style="color: #666;">Hi ${customer.name || 'there'},</p>
                  <p style="color: #666;">Please review and sign your quote by clicking the button below:</p>
                  <div style="text-align: center; margin: 2rem 0;">
                    <a href="${quoteUrl}" style="background: #F05A28; color: white; padding: 1rem 2rem; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
                      View & Sign Quote
                    </a>
                  </div>
                  <p style="color: #999; font-size: 0.9rem;">Or copy this link: ${quoteUrl}</p>
                </div>
              `,
              companyId: companyId
            })
          });
          if (emailResponse.ok) emailsSent++;
        }
      }

      // Send SMS
      if (sendQuoteMethod === 'sms' || sendQuoteMethod === 'both') {
        if (textingEnabled) {
          for (const phone of allPhones) {
            const smsResponse = await fetch('/api/sms-send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: phone,
                message: `You have a new quote ready for review. View and sign here: ${quoteUrl}`,
                customerId: customer.id,
                companyId: companyId
              })
            });
            if (smsResponse.ok) smsSent++;
          }
        } else {
          // No texting subscription — open native SMS app
          const smsBody = encodeURIComponent(`You have a new quote ready for review. View and sign here: ${quoteUrl}`);
          const cleanPhone = (allPhones[0] || '').replace(/\D/g, '');
          window.location.href = `sms:${cleanPhone}?body=${smsBody}`;
          smsSent = 1;
        }
      }

      // Update quote status to 'sent'
      await supabaseData
        .from('quotes')
        .update({ status: 'sent', sent_at: new Date().toISOString() })
        .eq('id', quoteToSend.id);

      if (emailsSent > 0 || smsSent > 0) {
        const methods = [];
        if (emailsSent > 0) methods.push(`${emailsSent} email${emailsSent > 1 ? 's' : ''}`);
        if (smsSent > 0) methods.push(`${smsSent} SMS`);
        alert(`Quote sent via ${methods.join(' and ')}!`);
        setShowSendQuoteModal(false);
        loadData();
      } else {
        alert('Failed to send quote. Please add an email or phone number.');
      }
    } catch (err) {
      alert('Error sending quote: ' + err.message);
    } finally {
      setSendingQuote(false);
    }
  };

  const createJobFromQuote = async (quote, schedDate, schedTime) => {
    setCreatingJob(true);
    try {
      // Generate job number
      const timestamp = Date.now().toString(36).toUpperCase();
      const random = Math.random().toString(36).substring(2, 5).toUpperCase();
      const jobNumber = `JOB-${timestamp}-${random}`;

      // Create the job
      const { data: job, error: jobError } = await supabaseData
        .from('jobs')
        .insert([{
          company_id: companyId,
          job_number: jobNumber,
          customer_id: quote.customer_id,
          quote_id: quote.id,
          job_type: 'repair',
          scheduled_date: schedDate,
          scheduled_time: schedTime,
          estimated_duration: 2,
          status: 'scheduled',
          quoted_value: quote.total,
          notes: `From Quote ${quote.quote_number}`,
          deposit_required: quote.deposit_required || false,
          deposit_amount: quote.deposit_amount || 0,
          deposit_paid: quote.deposit_paid || false,
          deposit_paid_at: quote.deposit_paid ? (quote.deposit_paid_at || new Date().toISOString()) : null
        }])
        .select()
        .single();

      if (jobError) throw jobError;

      // Copy quote items to job_items
      const { data: quoteItems } = await supabaseData
        .from('quote_items')
        .select('*')
        .eq('quote_id', quote.id);

      if (quoteItems && quoteItems.length > 0) {
        const jobItems = quoteItems.map(item => ({
          job_id: job.id,
          sku_id: item.sku_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          is_additional: false
        }));

        await supabaseData.from('job_items').insert(jobItems);
      }

      // Update quote status to 'scheduled'
      await supabaseData
        .from('quotes')
        .update({ status: 'scheduled' })
        .eq('id', quote.id);

      alert(`Job ${jobNumber} created successfully!`);
      setQuoteToCreateJob(null);
      setScheduleDate('');
      setScheduleTime('9:00');
      loadData();
    } catch (error) {
      alert('Error creating job: ' + error.message);
    } finally {
      setCreatingJob(false);
    }
  };

  // Create a follow-up job from a completed visit
  const createFollowUpJob = async (visit, schedDate, schedTime) => {
    setCreatingFollowUp(true);
    try {
      // Generate job number
      const timestamp = Date.now().toString(36).toUpperCase();
      const random = Math.random().toString(36).substring(2, 5).toUpperCase();
      const jobNumber = `JOB-${timestamp}-${random}`;

      // Create the follow-up job
      const { data: job, error: jobError } = await supabaseData
        .from('jobs')
        .insert([{
          company_id: companyId,
          job_number: jobNumber,
          customer_id: visit.customer_id,
          job_type: 'repair',
          scheduled_date: schedDate,
          scheduled_time: schedTime,
          estimated_duration: visit.estimated_duration || 60,
          status: 'scheduled',
          quoted_value: visit.quoted_value || null,
          notes: `Follow-up from visit ${visit.job_number}`
        }])
        .select()
        .single();

      if (jobError) throw jobError;

      // Copy any job items from the visit if they exist
      const { data: visitItems } = await supabaseData
        .from('job_items')
        .select('*')
        .eq('job_id', visit.id);

      if (visitItems && visitItems.length > 0) {
        const jobItems = visitItems.map(item => ({
          job_id: job.id,
          sku_id: item.sku_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          is_additional: false
        }));

        await supabaseData.from('job_items').insert(jobItems);
      }

      alert(`Follow-up job ${jobNumber} created successfully!`);
      setVisitToScheduleFollowUp(null);
      setScheduleDate('');
      setScheduleTime('9:00');
      loadData();
    } catch (error) {
      alert('Error creating follow-up job: ' + error.message);
    } finally {
      setCreatingFollowUp(false);
    }
  };

  const handleScheduleJob = async () => {
    if (!jobToSchedule || !scheduleDate) return;

    try {
      setSaving(true);
      const { error } = await supabaseData
        .from('jobs')
        .update({
          scheduled_date: scheduleDate,
          scheduled_time: scheduleTime,
          status: 'scheduled'
        })
        .eq('id', jobToSchedule.id);

      if (error) throw error;

      alert('Job scheduled successfully!');
      setShowScheduleModal(false);
      setJobToSchedule(null);
      setScheduleDate('');
      setScheduleTime('9:00');
      loadData();
    } catch (error) {
      alert('Error scheduling job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateJob = async (jobId, updates) => {
    try {
      setSaving(true);

      // Calculate total from items if we have them
      if (editingJobItems.length > 0) {
        const itemsTotal = editingJobItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
        updates.quoted_value = itemsTotal;
      }

      const { error } = await supabaseData
        .from('jobs')
        .update(updates)
        .eq('id', jobId);

      if (error) throw error;

      setEditingJob(null);
      setEditingJobItems([]);
      loadData();
    } catch (error) {
      alert('Error updating job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Load job items when editing modal opens
  const loadJobItems = async (jobId) => {
    setLoadingJobItems(true);
    try {
      const { data, error } = await supabaseData
        .from('job_items')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at');

      if (!error && data) {
        setEditingJobItems(data);
      }
    } catch (error) {
    } finally {
      setLoadingJobItems(false);
    }
  };

  // Open job editing modal with items
  const openEditJobModal = async (job) => {
    setEditingJob(job);
    await loadJobItems(job.id);
  };

  // Add new job item
  const addJobItem = async () => {
    if (!newJobItemName.trim() || parseFloat(newJobItemPrice) <= 0) {
      alert('Please enter a name and valid price');
      return;
    }

    const price = parseFloat(newJobItemPrice);
    const qty = parseInt(newJobItemQty) || 1;

    const { data, error } = await supabaseData
      .from('job_items')
      .insert([{
        job_id: editingJob.id,
        name: newJobItemName.trim(),
        quantity: qty,
        unit_price: price,
        total_price: price * qty,
        is_additional: true
      }])
      .select()
      .single();

    if (!error && data) {
      setEditingJobItems([...editingJobItems, data]);
      setNewJobItemName('');
      setNewJobItemPrice('');
      setNewJobItemQty(1);
      setShowAddJobItem(false);

      // Update the quoted_value in editingJob state
      const newTotal = [...editingJobItems, data].reduce((sum, item) => sum + (item.total_price || 0), 0);
      setEditingJob({ ...editingJob, quoted_value: newTotal });
    }
  };

  // Update job item
  const updateJobItem = async (itemId, updates) => {
    const { error } = await supabaseData
      .from('job_items')
      .update(updates)
      .eq('id', itemId);

    if (!error) {
      const updatedItems = editingJobItems.map(i => i.id === itemId ? { ...i, ...updates } : i);
      setEditingJobItems(updatedItems);

      // Update the quoted_value in editingJob state
      const newTotal = updatedItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
      setEditingJob({ ...editingJob, quoted_value: newTotal });
    }
  };

  // Remove job item
  const removeJobItem = async (itemId) => {
    const confirmed = await showConfirm('Remove Item', 'Remove this item from the job?');
    if (!confirmed) return;

    await supabaseData.from('job_items').delete().eq('id', itemId);
    const updatedItems = editingJobItems.filter(i => i.id !== itemId);
    setEditingJobItems(updatedItems);

    // Update the quoted_value in editingJob state
    const newTotal = updatedItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
    setEditingJob({ ...editingJob, quoted_value: newTotal });
  };

  // Add product from dropdown to job
  const addProductToJob = async (sku) => {
    const price = parseFloat(sku.price) || parseFloat(sku.base_price) || parseFloat(sku.base_cost) || 0;
    const name = sku.name;

    const { data, error } = await supabaseData
      .from('job_items')
      .insert([{
        job_id: editingJob.id,
        name: name,
        quantity: 1,
        unit_price: price,
        total_price: price,
        is_additional: true
      }])
      .select()
      .single();

    if (!error && data) {
      setEditingJobItems([...editingJobItems, data]);
      setShowProductDropdown(false);

      const newTotal = [...editingJobItems, data].reduce((sum, item) => sum + (item.total_price || 0), 0);
      setEditingJob({ ...editingJob, quoted_value: newTotal });
    }
  };

  // ===== QUOTE ITEMS FUNCTIONS =====

  // Load quote items when editing modal opens
  const loadQuoteItems = async (quoteId) => {
    setLoadingQuoteItems(true);
    try {
      const { data, error } = await supabaseData
        .from('quote_items')
        .select('*')
        .eq('quote_id', quoteId)
        .order('created_at');

      if (!error && data) {
        setEditingQuoteItems(data);
      }
    } catch (error) {
    } finally {
      setLoadingQuoteItems(false);
    }
  };

  // Open quote editing modal with items
  const openEditQuoteModal = async (quote) => {
    setEditingQuote(quote);
    await loadQuoteItems(quote.id);
  };

  // Add new quote item (manual)
  const addQuoteItem = async () => {
    if (!newQuoteItemName.trim() || parseFloat(newQuoteItemPrice) <= 0) {
      alert('Please enter a name and valid price');
      return;
    }

    const price = parseFloat(newQuoteItemPrice);
    const qty = parseInt(newQuoteItemQty) || 1;

    const { data, error } = await supabaseData
      .from('quote_items')
      .insert([{
        quote_id: editingQuote.id,
        name: newQuoteItemName.trim(),
        quantity: qty,
        unit_price: price,
        total_price: price * qty
      }])
      .select()
      .single();

    if (!error && data) {
      const newItems = [...editingQuoteItems, data];
      setEditingQuoteItems(newItems);
      setNewQuoteItemName('');
      setNewQuoteItemPrice('');
      setNewQuoteItemQty(1);
      setShowAddQuoteItem(false);

      // Update the total in editingQuote state
      const newTotal = newItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
      setEditingQuote({ ...editingQuote, total: newTotal });

      // Also update in database
      await supabaseData.from('quotes').update({ total: newTotal }).eq('id', editingQuote.id);
    }
  };

  // Add product from dropdown to quote
  const addProductToQuote = async (sku) => {
    const price = parseFloat(sku.price) || parseFloat(sku.base_price) || parseFloat(sku.base_cost) || 0;
    const name = sku.name;

    const { data, error } = await supabaseData
      .from('quote_items')
      .insert([{
        quote_id: editingQuote.id,
        name: name,
        quantity: 1,
        unit_price: price,
        total_price: price
      }])
      .select()
      .single();

    if (!error && data) {
      const newItems = [...editingQuoteItems, data];
      setEditingQuoteItems(newItems);
      setShowQuoteProductDropdown(false);

      const newTotal = newItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
      setEditingQuote({ ...editingQuote, total: newTotal });

      await supabaseData.from('quotes').update({ total: newTotal }).eq('id', editingQuote.id);
    }
  };

  // Update quote item
  const updateQuoteItem = async (itemId, updates) => {
    const { error } = await supabaseData
      .from('quote_items')
      .update(updates)
      .eq('id', itemId);

    if (!error) {
      const updatedItems = editingQuoteItems.map(i => i.id === itemId ? { ...i, ...updates } : i);
      setEditingQuoteItems(updatedItems);

      const newTotal = updatedItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
      setEditingQuote({ ...editingQuote, total: newTotal });

      await supabaseData.from('quotes').update({ total: newTotal }).eq('id', editingQuote.id);
    }
  };

  // Remove quote item
  const removeQuoteItem = async (itemId) => {
    const confirmed = await showConfirm('Remove Item', 'Remove this item from the quote?');
    if (!confirmed) return;

    await supabaseData.from('quote_items').delete().eq('id', itemId);
    const updatedItems = editingQuoteItems.filter(i => i.id !== itemId);
    setEditingQuoteItems(updatedItems);

    const newTotal = updatedItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
    setEditingQuote({ ...editingQuote, total: newTotal });

    await supabaseData.from('quotes').update({ total: newTotal }).eq('id', editingQuote.id);
  };

  const handleUpdateQuote = async (quoteId, updates) => {
    try {
      setSaving(true);
      const { error } = await supabaseData
        .from('quotes')
        .update(updates)
        .eq('id', quoteId);

      if (error) throw error;

      setEditingQuote(null);
      loadData();
    } catch (error) {
      alert('Error updating quote: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const openScheduleModal = (job) => {
    setJobToSchedule(job);
    setScheduleDate(job.scheduled_date || '');
    setScheduleTime(job.scheduled_time?.substring(0, 5) || '9:00');
    setShowScheduleModal(true);
  };

  const openCompletionModal = (job) => {
    setJobToComplete(job);
    setShowCompletionModal(true);
  };

  const handleJobCompleted = (invoice) => {
    // Refresh the jobs list after completion
    loadData();
    // Optionally navigate to invoicing
    if (invoice && onNavigate) {
      // Could navigate to invoicing: onNavigate('invoicing');
    }
  };

  // Load company info for invoice display
  const loadCompanyInfo = async () => {
    try {
      const { data } = await supabaseData
        .from('companies')
        .select('id, name, phone, email, address, city, state, zip, invoice_payment_terms, invoice_footer_text, invoice_notes')
        .eq('id', companyId)
        .single();
      setCompanyInfo(data);
    } catch (error) {
    }
  };

  // View invoice for a completed job
  const viewJobInvoice = async (job) => {
    if (!job.invoice_id) {
      alert('No invoice found for this job');
      return;
    }

    setLoadingInvoice(true);
    try {
      // Load company info if not already loaded
      if (!companyInfo) {
        await loadCompanyInfo();
      }

      // Load the invoice
      const { data: invoice, error } = await supabaseData
        .from('invoices')
        .select(`
          *,
          customers (
            id, name, first_name, last_name, phone, email, address, city, state, zip
          )
        `)
        .eq('id', job.invoice_id)
        .single();

      if (error) throw error;

      // Calculate customer name and balance
      const customerName = invoice.customers?.name ||
        `${invoice.customers?.first_name || ''} ${invoice.customers?.last_name || ''}`.trim() ||
        'Unknown';
      const balance = invoice.total - (invoice.amount_paid || 0);

      setViewingInvoice({ ...invoice, customerName, balance });

      // Load invoice items
      const { data: items } = await supabaseData
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', job.invoice_id)
        .order('created_at');

      setInvoiceItems(items || []);
      setShowInvoiceModal(true);
    } catch (error) {
      alert('Error loading invoice: ' + error.message);
    } finally {
      setLoadingInvoice(false);
    }
  };

  // Generate PDF for invoice
  const generateInvoicePDF = (invoice, items) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Company header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(companyInfo?.name || 'Company', 20, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (companyInfo?.phone) {
      doc.text(companyInfo.phone, 20, y);
      y += 5;
    }
    if (companyInfo?.email) {
      doc.text(companyInfo.email, 20, y);
      y += 5;
    }
    if (companyInfo?.address) {
      const addr = `${companyInfo.address}${companyInfo.city ? ', ' + companyInfo.city : ''}${companyInfo.state ? ', ' + companyInfo.state : ''} ${companyInfo.zip || ''}`;
      doc.text(addr, 20, y);
    }

    // Invoice title
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageWidth - 20, 25, { align: 'right' });

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice #: ${invoice.invoice_number}`, pageWidth - 20, 35, { align: 'right' });
    doc.text(`Date: ${formatDisplayDate(invoice.created_at?.split('T')[0])}`, pageWidth - 20, 42, { align: 'right' });
    doc.text(`Due: ${formatDisplayDate(invoice.due_date)}`, pageWidth - 20, 49, { align: 'right' });

    // Bill To
    y = 70;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 20, y);
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(invoice.customerName, 20, y);
    y += 5;

    if (invoice.customers?.phone) {
      doc.text(invoice.customers.phone, 20, y);
      y += 5;
    }
    if (invoice.customers?.email) {
      doc.text(invoice.customers.email, 20, y);
      y += 5;
    }

    // Items table header
    y += 15;
    doc.setFillColor(51, 51, 51);
    doc.rect(20, y - 5, pageWidth - 40, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('Description', 25, y + 2);
    doc.text('Qty', 120, y + 2);
    doc.text('Price', 145, y + 2);
    doc.text('Total', pageWidth - 25, y + 2, { align: 'right' });

    // Items
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    y += 12;

    if (items && items.length > 0) {
      items.forEach(item => {
        doc.text(item.description || item.name || 'Item', 25, y);
        doc.text(String(item.quantity || 1), 125, y);
        doc.text(`$${(item.unit_price || item.price || 0).toFixed(2)}`, 145, y);
        doc.text(`$${((item.quantity || 1) * (item.unit_price || item.price || 0)).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
        y += 8;
      });
    } else {
      doc.text('Services rendered', 25, y);
      doc.text('1', 125, y);
      doc.text(`$${(invoice.subtotal || invoice.total || 0).toFixed(2)}`, 145, y);
      doc.text(`$${(invoice.subtotal || invoice.total || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
      y += 8;
    }

    // Totals
    y += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(120, y, pageWidth - 20, y);
    y += 8;

    doc.text('Subtotal:', 120, y);
    doc.text(`$${(invoice.subtotal || invoice.total || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
    y += 7;

    if (invoice.tax_amount > 0) {
      doc.text(`Tax:`, 120, y);
      doc.text(`$${(invoice.tax_amount || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
      y += 7;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Total:', 120, y);
    doc.text(`$${(invoice.total || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
    y += 8;

    if (invoice.amount_paid > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(0, 128, 0);
      doc.text('Paid:', 120, y);
      doc.text(`$${(invoice.amount_paid || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
      y += 7;

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Balance Due:', 120, y);
      doc.text(`$${(invoice.balance || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
    }

    // Notes
    const invoiceNotes = companyInfo?.invoice_notes;
    if (invoiceNotes) {
      y += 15;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text('Notes:', 20, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      const noteLines = doc.splitTextToSize(invoiceNotes, pageWidth - 40);
      doc.text(noteLines, 20, y);
    }

    // Payment terms
    const paymentTerms = companyInfo?.invoice_payment_terms;
    if (paymentTerms && paymentTerms !== 'Due on Receipt') {
      y += 15;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Payment Terms: ${paymentTerms}`, 20, y);
    }

    // Footer
    y = doc.internal.pageSize.getHeight() - 30;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    const footerText = companyInfo?.invoice_footer_text || 'Thank you for your business!';
    doc.text(footerText, pageWidth / 2, y, { align: 'center' });

    return doc;
  };

  // Download PDF
  const downloadInvoicePDF = () => {
    if (!viewingInvoice) return;
    const doc = generateInvoicePDF(viewingInvoice, invoiceItems);
    doc.save(`Invoice-${viewingInvoice.invoice_number}.pdf`);
  };

  // Download Quote PDF
  const downloadQuotePDF = async (quote) => {
    try {
      const { data: items } = await supabaseData
        .from('quote_items')
        .select('*')
        .eq('quote_id', quote.id)
        .order('created_at');

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 20;

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Quote', 20, y);
      doc.setFontSize(11);
      doc.text(`#${quote.quote_number}`, pageWidth - 20, y, { align: 'right' });
      y += 10;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Date: ${new Date(quote.created_at).toLocaleDateString()}`, 20, y);
      const statusText = (quote.status || 'draft').charAt(0).toUpperCase() + (quote.status || 'draft').slice(1);
      doc.text(`Status: ${statusText}`, pageWidth - 20, y, { align: 'right' });
      y += 8;

      // Customer
      const custName = getCustomerName(quote.customers);
      doc.setFont('helvetica', 'bold');
      doc.text('Customer:', 20, y);
      doc.setFont('helvetica', 'normal');
      doc.text(custName, 55, y);
      y += 6;
      if (quote.customers?.phone) { doc.text(`Phone: ${quote.customers.phone}`, 20, y); y += 5; }
      if (quote.customers?.email) { doc.text(`Email: ${quote.customers.email}`, 20, y); y += 5; }
      y += 8;

      // Items table
      if (items && items.length > 0) {
        doc.setFillColor(50, 50, 50);
        doc.rect(20, y, pageWidth - 40, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('Item', 22, y + 5.5);
        doc.text('Qty', pageWidth - 75, y + 5.5);
        doc.text('Price', pageWidth - 55, y + 5.5);
        doc.text('Total', pageWidth - 30, y + 5.5, { align: 'right' });
        y += 10;
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        items.forEach((item) => {
          if (y > 260) { doc.addPage(); y = 20; }
          const qty = item.quantity || 1;
          const price = item.unit_price || item.price || 0;
          const total = qty * price;
          doc.text(item.name || item.description || 'Item', 22, y + 4);
          doc.text(String(qty), pageWidth - 75, y + 4);
          doc.text(`$${price.toFixed(2)}`, pageWidth - 55, y + 4);
          doc.text(`$${total.toFixed(2)}`, pageWidth - 30, y + 4, { align: 'right' });
          doc.setDrawColor(220, 220, 220);
          doc.line(20, y + 7, pageWidth - 20, y + 7);
          y += 9;
        });
        y += 4;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(`Total: $${(quote.total || 0).toFixed(2)}`, pageWidth - 30, y, { align: 'right' });

      if (quote.notes) {
        y += 10;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text('Notes:', 20, y);
        y += 5;
        const noteLines = doc.splitTextToSize(quote.notes, pageWidth - 40);
        doc.text(noteLines, 20, y);
      }

      doc.save(`Quote-${quote.quote_number}.pdf`);
      alert('Quote PDF downloaded!');
    } catch (error) {
      alert('Failed to download quote PDF');
    }
  };

  // Print invoice
  const printInvoice = () => {
    if (!viewingInvoice) return;
    const doc = generateInvoicePDF(viewingInvoice, invoiceItems);
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  // Copy payment link
  const copyPaymentLink = async () => {
    if (!viewingInvoice) return;
    const paymentUrl = `${window.location.origin}/pay/${viewingInvoice.id}`;
    try {
      await navigator.clipboard.writeText(paymentUrl);
      alert('Payment link copied!');
    } catch (err) {
      prompt('Payment link:', paymentUrl);
    }
  };

  // New Quote Form
  const NewQuoteForm = () => (
    <div style={{
      background: colors.bgCard,
      borderRadius: '12px',
      padding: '2rem',
      border: `1px solid ${colors.border}`
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        paddingBottom: '1rem',
        borderBottom: `2px solid ${colors.border}`
      }}>
        <h2 style={{ fontSize: '1.75rem', margin: 0, color: colors.text }}>
          📋 Create New Quote
        </h2>
        <button
          onClick={() => setViewMode('list')}
          style={{
            background: colors.bgInput,
            border: 'none',
            borderRadius: '8px',
            padding: '0.5rem',
            cursor: 'pointer',
            color: colors.textMuted
          }}
        >
          <X size={24} />
        </button>
      </div>

      <form onSubmit={handleCreateQuote}>
        <div style={{ display: 'grid', gap: '1.5rem', maxWidth: '600px' }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Customer *
            </label>
            <select
              value={quoteForm.customer_id}
              onChange={(e) => setQuoteForm({ ...quoteForm, customer_id: e.target.value })}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem'
              }}
            >
              <option value="">Select a customer...</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {getCustomerName(customer)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Notes
            </label>
            <textarea
              value={quoteForm.notes}
              onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
              rows={4}
              placeholder="Add any notes about this quote..."
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: '1rem',
            paddingTop: '1rem'
          }}>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              style={{
                flex: 1,
                padding: '1rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                flex: 1,
                padding: '1rem',
                background: saving ? colors.border : colors.info,
                border: 'none',
                borderRadius: '8px',
                color: saving ? '#666' : '#fff',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <Save size={20} />
              {saving ? 'Creating...' : 'Create Quote'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  // New Job Form
  const NewJobForm = () => (
    <div style={{
      background: colors.bgCard,
      borderRadius: '12px',
      padding: '2rem',
      border: `1px solid ${colors.border}`
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        paddingBottom: '1rem',
        borderBottom: `2px solid ${colors.border}`
      }}>
        <h2 style={{ fontSize: '1.75rem', margin: 0, color: colors.text }}>
          💼 Create New Job
        </h2>
        <button
          onClick={() => setViewMode('list')}
          style={{
            background: colors.bgInput,
            border: 'none',
            borderRadius: '8px',
            padding: '0.5rem',
            cursor: 'pointer',
            color: colors.textMuted
          }}
        >
          <X size={24} />
        </button>
      </div>

      <form onSubmit={handleCreateJob}>
        <div style={{ display: 'grid', gap: '1.5rem', maxWidth: '600px' }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Customer *
            </label>
            <select
              value={jobForm.customer_id}
              onChange={(e) => setJobForm({ ...jobForm, customer_id: e.target.value })}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem'
              }}
            >
              <option value="">Select a customer...</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {getCustomerName(customer)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Job Type *
            </label>
            <input
              type="text"
              value={jobForm.job_type}
              onChange={(e) => setJobForm({ ...jobForm, job_type: e.target.value })}
              required
              placeholder="e.g., Door Installation, Spring Repair"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.text,
                fontWeight: 'bold'
              }}>
                Scheduled Date
              </label>
              <input
                type="date"
                value={jobForm.scheduled_date}
                onChange={(e) => setJobForm({ ...jobForm, scheduled_date: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.text,
                fontWeight: 'bold'
              }}>
                Scheduled Time
              </label>
              <input
                type="time"
                value={jobForm.scheduled_time}
                onChange={(e) => setJobForm({ ...jobForm, scheduled_time: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Notes
            </label>
            <textarea
              value={jobForm.notes}
              onChange={(e) => setJobForm({ ...jobForm, notes: e.target.value })}
              rows={4}
              placeholder="Add any notes about this job..."
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: '1rem',
            paddingTop: '1rem'
          }}>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              style={{
                flex: 1,
                padding: '1rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                flex: 1,
                padding: '1rem',
                background: saving ? colors.border : colors.accent,
                border: 'none',
                borderRadius: '8px',
                color: saving ? '#666' : '#fff',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <Save size={20} />
              {saving ? 'Creating...' : 'Create Job'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  const QuoteCard = ({ quote }) => (
    <div
      onClick={() => openEditQuoteModal(quote)}
      style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1.5rem',
        border: `1px solid ${colors.border}`,
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseOver={(e) => e.currentTarget.style.borderColor = colors.accent}
      onMouseOut={(e) => e.currentTarget.style.borderColor = colors.border}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'start',
        marginBottom: '1rem'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
            {quote.quote_number}
          </div>
          <h3 style={{ fontSize: '1.25rem', margin: '0 0 0.5rem 0', color: colors.text }}>
            {getCustomerName(quote.customers)}
          </h3>
        </div>
        <div style={{
          padding: '0.35rem 0.75rem',
          background: `${getStatusColor(quote.status)}20`,
          borderRadius: '6px',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          color: getStatusColor(quote.status),
          textTransform: 'capitalize'
        }}>
          {quote.status}
        </div>
      </div>

      <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>
        ${quote.total?.toLocaleString() || '0.00'}
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        marginBottom: '1rem',
        paddingBottom: '1rem',
        borderBottom: `1px solid ${colors.border}`
      }}>
        {quote.customers?.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
            <User size={14} />
            {quote.customers.phone}
          </div>
        )}
        {quote.customers?.address && (
          <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
            <MapPin size={14} style={{ marginTop: '2px' }} />
            <span>
              {quote.customers.address}
              {quote.customers.city && `, ${quote.customers.city}`}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
          <Clock size={14} />
          Created {formatDisplayDate(quote.created_at?.split('T')[0])}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
        {/* View Quote */}
        {(quote.status === 'sent' || quote.status === 'signed') && (
          <button
            onClick={() => window.open(getQuoteUrl(quote.id), '_blank')}
            style={{
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              color: colors.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            title="View Quote"
          >
            <Eye size={16} />
            <span style={{ fontSize: '0.85rem' }}>View</span>
          </button>
        )}

        {/* View Signature (for signed quotes) */}
        {quote.status === 'signed' && (quote.signature_data || quote.signature_url) && (
          <button
            onClick={() => setViewingSignature(quote)}
            style={{
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              color: colors.info,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            title="View Signature"
          >
            <Eye size={16} />
            <span style={{ fontSize: '0.85rem' }}>Signature</span>
          </button>
        )}

        {/* Send Quote */}
        <button
          onClick={() => openSendQuoteModal(quote)}
          style={{
            padding: '0.75rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            color: colors.accent,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          title="Send Quote"
        >
          <Send size={16} />
          <span style={{ fontSize: '0.85rem' }}>Send</span>
        </button>

        {/* Schedule Job (for signed quotes) */}
        {quote.status === 'signed' && (
          <button
            onClick={() => {
              // Navigate to calendar with quote data for scheduling
              if (onNavigate) {
                onNavigate('schedule', {
                  createJobFromQuote: quote,
                  customer: quote.customers
                });
              }
            }}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: colors.accent,
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.85rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <Calendar size={16} />
            Schedule Job
          </button>
        )}

        {/* Download PDF */}
        <button
          onClick={async () => { setPdfLoading(quote.id); try { await downloadQuotePDF(quote); } finally { setPdfLoading(null); } }}
          disabled={pdfLoading === quote.id}
          style={{
            padding: '0.75rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            color: colors.text,
            cursor: pdfLoading === quote.id ? 'wait' : 'pointer',
            opacity: pdfLoading === quote.id ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          title="Download PDF"
        >
          {pdfLoading === quote.id ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
          ) : (
            <Download size={16} />
          )}
          <span style={{ fontSize: '0.85rem' }}>{pdfLoading === quote.id ? 'Generating...' : 'PDF'}</span>
        </button>

        {/* Delete */}
        <button
          onClick={() => handleDelete(quote, 'quote')}
          style={{
            padding: '0.75rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            color: colors.error,
            cursor: 'pointer'
          }}
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  const JobCard = ({ job }) => (
    <div
      onClick={() => openEditJobModal(job)}
      style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1.5rem',
        border: `1px solid ${colors.border}`,
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseOver={(e) => e.currentTarget.style.borderColor = colors.accent}
      onMouseOut={(e) => e.currentTarget.style.borderColor = colors.border}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'start',
        marginBottom: '1rem'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
            {job.job_number}
          </div>
          <h3 style={{ fontSize: '1.25rem', margin: '0 0 0.5rem 0', color: colors.text }}>
            {getCustomerName(job.customers)}
          </h3>
        </div>
        <div style={{
          padding: '0.35rem 0.75rem',
          background: `${getStatusColor(job.status)}20`,
          borderRadius: '6px',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          color: getStatusColor(job.status),
          textTransform: 'capitalize'
        }}>
          {job.status?.replace('-', ' ')}
        </div>
      </div>

      {job.scheduled_date && (
        <div style={{
          background: colors.bgInput,
          padding: '0.75rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <Calendar size={20} color={colors.info} />
          <div>
            <div style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.95rem' }}>
              {formatDisplayDate(job.scheduled_date)}
            </div>
            {job.scheduled_time && (
              <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                {job.scheduled_time.substring(0, 5)}
              </div>
            )}
          </div>
        </div>
      )}

      {job.quoted_value && (
        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>
          ${job.quoted_value?.toLocaleString() || '0.00'}
        </div>
      )}

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        marginBottom: '1rem',
        paddingBottom: '1rem',
        borderBottom: `1px solid ${colors.border}`
      }}>
        {job.customers?.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
            <User size={14} />
            {job.customers.phone}
          </div>
        )}
        {job.customers?.address && (
          <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
            <MapPin size={14} style={{ marginTop: '2px' }} />
            <span>
              {job.customers.address}
              {job.customers.city && `, ${job.customers.city}`}
            </span>
          </div>
        )}
        {job.job_type && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
            <Briefcase size={14} />
            {job.job_type}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
        {job.status !== 'completed' ? (
          <>
            <button
              onClick={() => openCompletionModal(job)}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: colors.success,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.85rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <CheckCircle size={16} />
              Complete & Invoice
            </button>
            <button
              onClick={() => {
                setJobToPlan(job);
                setShowPlannerModal(true);
              }}
              style={{
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                color: colors.text
              }}
              title="Plan Job Materials"
            >
              <ClipboardList size={16} />
            </button>
            <button
              onClick={() => openScheduleModal(job)}
              style={{
                padding: '0.75rem',
                background: colors.info,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              title={job.scheduled_date ? 'Reschedule' : 'Schedule'}
            >
              <Calendar size={16} />
            </button>
          </>
        ) : (
          <>
            {job.job_type === 'quote_visit' && (
              <button
                onClick={() => {
                  setVisitToScheduleFollowUp(job);
                  setScheduleDate('');
                  setScheduleTime('9:00');
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: colors.success,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Calendar size={16} />
                Schedule Follow-Up
              </button>
            )}
            <button
              onClick={() => viewJobInvoice(job)}
              disabled={loadingInvoice}
              style={{
                flex: job.job_type === 'quote_visit' ? 'none' : 1,
                padding: '0.75rem',
                background: colors.accent,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.85rem',
                fontWeight: 'bold',
                cursor: loadingInvoice ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <Eye size={16} />
              {loadingInvoice ? 'Loading...' : 'View Invoice'}
            </button>
          </>
        )}
        <button
          onClick={() => handleDelete(job, 'job')}
          style={{
            padding: '0.75rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            color: colors.error,
            cursor: 'pointer'
          }}
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading...
      </div>
    );
  }

  // Show list view
  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{ fontSize: '2rem', margin: '0 0 0.5rem 0', color: colors.text }}>
            📋 Work Orders
          </h1>
          <p style={{ margin: 0, color: colors.textMuted }}>
            Manage your quotes and jobs
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => onNavigate('express-job', { workflowType: 'full' })}
            style={{
              padding: '0.75rem 1.5rem',
              background: colors.accent,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Plus size={20} />
            New Job
          </button>
        </div>
      </div>

      {/* Main Tabs */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        marginBottom: '1rem',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden'
      }}>
        <div style={{ display: 'flex' }}>
          {!isFieldTech && (
          <button
            onClick={() => { setActiveTab('quotes'); setQuoteStatusFilter('all'); }}
            style={{
              flex: 1,
              padding: isMobile ? '0.75rem 0.5rem' : '1.25rem',
              background: activeTab === 'quotes' ? colors.info : 'transparent',
              color: activeTab === 'quotes' ? '#fff' : colors.textMuted,
              border: 'none',
              borderBottom: activeTab === 'quotes' ? 'none' : `2px solid ${colors.border}`,
              fontSize: isMobile ? '0.85rem' : '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '0.35rem' : '0.75rem',
              transition: 'all 0.2s'
            }}
          >
            <FileText size={isMobile ? 16 : 22} />
            Quotes
            <span style={{
              background: activeTab === 'quotes' ? 'rgba(255,255,255,0.2)' : colors.bgInput,
              padding: isMobile ? '0.15rem 0.5rem' : '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: isMobile ? '0.75rem' : '0.85rem'
            }}>
              {quotes.length}
            </span>
          </button>
          )}
          <button
            onClick={() => { setActiveTab('jobs'); setJobStatusFilter('all'); }}
            style={{
              flex: 1,
              padding: isMobile ? '0.75rem 0.5rem' : '1.25rem',
              background: activeTab === 'jobs' ? colors.accent : 'transparent',
              color: activeTab === 'jobs' ? '#fff' : colors.textMuted,
              border: 'none',
              borderBottom: activeTab === 'jobs' ? 'none' : `2px solid ${colors.border}`,
              fontSize: isMobile ? '0.85rem' : '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '0.35rem' : '0.75rem',
              transition: 'all 0.2s'
            }}
          >
            <Briefcase size={isMobile ? 16 : 22} />
            Jobs
            <span style={{
              background: activeTab === 'jobs' ? 'rgba(255,255,255,0.2)' : colors.bgInput,
              padding: isMobile ? '0.15rem 0.5rem' : '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: isMobile ? '0.75rem' : '0.85rem'
            }}>
              {jobs.length}
            </span>
          </button>
          {!isFieldTech && (
          <button
            onClick={() => { setActiveTab('invoices'); }}
            style={{
              flex: 1,
              padding: isMobile ? '0.75rem 0.5rem' : '1.25rem',
              background: activeTab === 'invoices' ? colors.success : 'transparent',
              color: activeTab === 'invoices' ? '#fff' : colors.textMuted,
              border: 'none',
              borderBottom: activeTab === 'invoices' ? 'none' : `2px solid ${colors.border}`,
              fontSize: isMobile ? '0.85rem' : '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '0.35rem' : '0.75rem',
              transition: 'all 0.2s'
            }}
          >
            <Receipt size={isMobile ? 16 : 22} />
            Invoices
          </button>
          )}
        </div>
      </div>

      {/* Invoices Tab Content */}
      {activeTab === 'invoices' && !isFieldTech && (
        <InvoicingModule companyId={companyId} onNavigate={onNavigate} embedded={true} />
      )}

      {/* Quotes/Jobs Tab Content */}
      {activeTab !== 'invoices' && (
      <>
      {/* Status Filters, Search, Sort */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1rem 1.5rem',
        marginBottom: '2rem',
        border: `1px solid ${colors.border}`
      }}>
        {/* Status sub-filters */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: `1px solid ${colors.border}`
        }}>
          {activeTab === 'quotes' ? (
            <>
              {[
                { key: 'all', label: 'All', count: getQuoteStatusCounts().all },
                { key: 'draft', label: 'Draft', count: getQuoteStatusCounts().draft },
                { key: 'sent', label: 'Sent', count: getQuoteStatusCounts().sent },
                { key: 'signed', label: 'Signed', count: getQuoteStatusCounts().signed }
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => setQuoteStatusFilter(filter.key)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: quoteStatusFilter === filter.key ? colors.info : colors.bgInput,
                    color: quoteStatusFilter === filter.key ? '#fff' : colors.textMuted,
                    border: `1px solid ${quoteStatusFilter === filter.key ? colors.info : colors.border}`,
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s'
                  }}
                >
                  {filter.label}
                  <span style={{
                    background: quoteStatusFilter === filter.key ? 'rgba(255,255,255,0.25)' : colors.border,
                    padding: '0.125rem 0.5rem',
                    borderRadius: '10px',
                    fontSize: '0.75rem'
                  }}>
                    {filter.count}
                  </span>
                </button>
              ))}
            </>
          ) : (
            <>
              {[
                { key: 'all', label: 'All', count: getJobStatusCounts().all },
                { key: 'unscheduled', label: 'Unscheduled', count: getJobStatusCounts().unscheduled },
                { key: 'scheduled', label: 'Scheduled', count: getJobStatusCounts().scheduled },
                { key: 'in-progress', label: 'In Progress', count: getJobStatusCounts()['in-progress'] },
                { key: 'completed', label: 'Completed', count: getJobStatusCounts().completed }
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => setJobStatusFilter(filter.key)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: jobStatusFilter === filter.key ? colors.accent : colors.bgInput,
                    color: jobStatusFilter === filter.key ? '#fff' : colors.textMuted,
                    border: `1px solid ${jobStatusFilter === filter.key ? colors.accent : colors.border}`,
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s'
                  }}
                >
                  {filter.label}
                  <span style={{
                    background: jobStatusFilter === filter.key ? 'rgba(255,255,255,0.25)' : colors.border,
                    padding: '0.125rem 0.5rem',
                    borderRadius: '10px',
                    fontSize: '0.75rem'
                  }}>
                    {filter.count}
                  </span>
                </button>
              ))}
            </>
          )}
        </div>

        {/* Search and Sort row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 'min(200px, 100%)', maxWidth: '400px' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textMuted
              }}
            />
            <input
              type="text"
              placeholder="Search by customer, number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.9rem'
              }}
            />
          </div>

          {/* Sort dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ArrowUpDown size={16} color={colors.textMuted} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="value-high">Value: High to Low</option>
              <option value="value-low">Value: Low to High</option>
              <option value="name">Customer Name</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results summary */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        padding: '0 0.25rem'
      }}>
        <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
          Showing {filteredData.length} {activeTab === 'quotes' ? 'quote' : 'job'}{filteredData.length !== 1 ? 's' : ''}
          {(activeTab === 'quotes' && quoteStatusFilter !== 'all') && ` (${quoteStatusFilter})`}
          {(activeTab === 'jobs' && jobStatusFilter !== 'all') && ` (${jobStatusFilter.replace('-', ' ')})`}
          {searchTerm && ` matching "${searchTerm}"`}
        </span>
      </div>

      {/* Grid */}
      {filteredData.length === 0 ? (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '4rem 2rem',
          textAlign: 'center',
          border: `2px dashed ${colors.border}`
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {activeTab === 'quotes' ? '📋' : '💼'}
          </div>
          <h3 style={{ color: colors.textMuted, marginBottom: '0.5rem' }}>
            {searchTerm ? `No ${activeTab} found` : `No ${activeTab} yet`}
          </h3>
          <p style={{ color: '#666' }}>
            {searchTerm
              ? `Try adjusting your search or filters`
              : activeTab === 'quotes'
                ? 'Create your first quote to get started'
                : 'Create your first job to get started'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={() => activeTab === 'quotes'
                ? onNavigate('quote-builder')
                : onNavigate('express-job', { workflowType: 'full' })
              }
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                background: colors.accent,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Plus size={18} />
              {activeTab === 'quotes' ? 'New Quote' : 'New Job'}
            </button>
          )}
          {(activeTab === 'quotes' && quoteStatusFilter !== 'all') && (
            <button
              onClick={() => setQuoteStatusFilter('all')}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                background: colors.info,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Show All Quotes
            </button>
          )}
          {(activeTab === 'jobs' && jobStatusFilter !== 'all') && (
            <button
              onClick={() => setJobStatusFilter('all')}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                background: colors.accent,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Show All Jobs
            </button>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
          gap: '1.5rem'
        }}>
          {filteredData.map(item =>
            activeTab === 'quotes'
              ? <QuoteCard key={item.id} quote={item} />
              : <JobCard key={item.id} job={item} />
          )}
        </div>
      )}
      </>
      )}

      {/* Schedule Job Modal */}
      {showScheduleModal && jobToSchedule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '2rem',
            width: '100%',
            maxWidth: '450px',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                Schedule Job
              </h3>
              <button
                onClick={() => { setShowScheduleModal(false); setJobToSchedule(null); }}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ marginBottom: '1rem', padding: '1rem', background: colors.bgInput, borderRadius: '8px' }}>
              <div style={{ fontWeight: 'bold', color: colors.text }}>{jobToSchedule.job_number}</div>
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>{getCustomerName(jobToSchedule.customers)}</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Date *</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Time</label>
                <select
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  {Array.from({ length: 96 }, (_, i) => {
                    const hour = Math.floor(i / 4);
                    const minute = (i % 4) * 15;
                    const timeStr = `${hour}:${minute.toString().padStart(2, '0')}`;
                    return <option key={timeStr} value={timeStr}>{timeStr}</option>;
                  })}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => { setShowScheduleModal(false); setJobToSchedule(null); }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleScheduleJob}
                  disabled={!scheduleDate || saving}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: scheduleDate ? colors.success : colors.border,
                    border: 'none',
                    borderRadius: '8px',
                    color: scheduleDate ? '#fff' : '#666',
                    cursor: scheduleDate ? 'pointer' : 'not-allowed',
                    fontWeight: '600'
                  }}
                >
                  {saving ? 'Saving...' : 'Schedule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Signature Modal */}
      {viewingSignature && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '500px',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                Quote Signature
              </h3>
              <button
                onClick={() => setViewingSignature(null)}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Quote Info */}
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              background: colors.bgInput,
              borderRadius: '8px',
              fontSize: '0.9rem'
            }}>
              <div style={{ color: colors.text, fontWeight: 'bold' }}>{viewingSignature.quote_number}</div>
              <div style={{ color: colors.textMuted }}>{getCustomerName(viewingSignature.customers)}</div>
              <div style={{ color: colors.success, fontWeight: 'bold' }}>
                ${(viewingSignature.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>

            {/* Signature Image */}
            <div style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              <img
                src={viewingSignature.signature_data || viewingSignature.signature_url}
                alt="Customer Signature"
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>

            {/* Signature Details */}
            <div style={{
              padding: '0.75rem',
              background: colors.success + '15',
              borderRadius: '8px',
              border: `1px solid ${colors.success}30`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                <CheckCircle size={16} color={colors.success} />
                <span style={{ color: colors.success, fontWeight: 'bold' }}>Signed</span>
              </div>
              <div style={{ color: colors.text, fontSize: '0.9rem' }}>
                <strong>By:</strong> {viewingSignature.signer_name || 'Unknown'}
              </div>
              {viewingSignature.signed_at && (
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  <strong>On:</strong> {new Date(viewingSignature.signed_at).toLocaleString()}
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={() => setViewingSignature(null)}
              style={{
                width: '100%',
                marginTop: '1rem',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Create Job from Quote Modal */}
      {quoteToCreateJob && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '2rem',
            width: '100%',
            maxWidth: '450px',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                📅 Schedule Job from Quote
              </h3>
              <button
                onClick={() => { setQuoteToCreateJob(null); setScheduleDate(''); setScheduleTime('9:00'); }}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ marginBottom: '1rem', padding: '1rem', background: colors.bgInput, borderRadius: '8px' }}>
              <div style={{ fontWeight: 'bold', color: colors.text }}>{quoteToCreateJob.quote_number}</div>
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>{getCustomerName(quoteToCreateJob.customers)}</div>
              <div style={{ color: colors.success, fontWeight: 'bold', marginTop: '0.5rem' }}>
                ${(quoteToCreateJob.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Date *</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Time</label>
                <select
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  {Array.from({ length: 49 }, (_, i) => {
                    const hour = Math.floor(i / 4) + 7;
                    const minute = (i % 4) * 15;
                    if (hour > 19) return null;
                    const timeStr = `${hour}:${minute.toString().padStart(2, '0')}`;
                    const displayStr = `${hour > 12 ? hour - 12 : hour}:${minute.toString().padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
                    return <option key={timeStr} value={timeStr}>{displayStr}</option>;
                  })}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => { setQuoteToCreateJob(null); setScheduleDate(''); setScheduleTime('9:00'); }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => createJobFromQuote(quoteToCreateJob, scheduleDate, scheduleTime)}
                  disabled={!scheduleDate || creatingJob}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: scheduleDate ? colors.success : colors.border,
                    border: 'none',
                    borderRadius: '8px',
                    color: scheduleDate ? '#fff' : '#666',
                    cursor: scheduleDate ? 'pointer' : 'not-allowed',
                    fontWeight: '600'
                  }}
                >
                  {creatingJob ? 'Creating...' : 'Create Job'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Follow-Up from Visit Modal */}
      {visitToScheduleFollowUp && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '2rem',
            width: '100%',
            maxWidth: '450px',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                📅 Schedule Follow-Up Job
              </h3>
              <button
                onClick={() => { setVisitToScheduleFollowUp(null); setScheduleDate(''); setScheduleTime('9:00'); }}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ marginBottom: '1rem', padding: '1rem', background: colors.bgInput, borderRadius: '8px' }}>
              <div style={{ fontWeight: 'bold', color: colors.text }}>Visit: {visitToScheduleFollowUp.job_number}</div>
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>{getCustomerName(visitToScheduleFollowUp.customers)}</div>
              {visitToScheduleFollowUp.quoted_value > 0 && (
                <div style={{ color: colors.success, fontWeight: 'bold', marginTop: '0.5rem' }}>
                  ${(visitToScheduleFollowUp.quoted_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Date *</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Time</label>
                <select
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  {Array.from({ length: 49 }, (_, i) => {
                    const hour = Math.floor(i / 4) + 7;
                    const minute = (i % 4) * 15;
                    if (hour > 19) return null;
                    const timeStr = `${hour}:${minute.toString().padStart(2, '0')}`;
                    const displayStr = `${hour > 12 ? hour - 12 : hour}:${minute.toString().padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
                    return <option key={timeStr} value={timeStr}>{displayStr}</option>;
                  })}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => { setVisitToScheduleFollowUp(null); setScheduleDate(''); setScheduleTime('9:00'); }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => createFollowUpJob(visitToScheduleFollowUp, scheduleDate, scheduleTime)}
                  disabled={!scheduleDate || creatingFollowUp}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: scheduleDate ? colors.success : colors.border,
                    border: 'none',
                    borderRadius: '8px',
                    color: scheduleDate ? '#fff' : '#666',
                    cursor: scheduleDate ? 'pointer' : 'not-allowed',
                    fontWeight: '600'
                  }}
                >
                  {creatingFollowUp ? 'Creating...' : 'Create Follow-Up Job'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem',
          overflow: 'auto'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '2rem',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                Edit Job: {editingJob.job_number}
              </h3>
              <button
                onClick={() => {
                  setEditingJob(null);
                  setEditingJobItems([]);
                  setShowAddJobItem(false);
                }}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Status</label>
                <select
                  value={editingJob.status || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  <option value="unscheduled">Unscheduled</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Job Type</label>
                <input
                  type="text"
                  value={editingJob.job_type || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, job_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              {/* Scheduled Date/Time */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Scheduled Date</label>
                  <input
                    type="date"
                    value={editingJob.scheduled_date || ''}
                    onChange={(e) => setEditingJob({ ...editingJob, scheduled_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Scheduled Time</label>
                  <input
                    type="time"
                    value={editingJob.scheduled_time || ''}
                    onChange={(e) => setEditingJob({ ...editingJob, scheduled_time: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              {/* Job Items Section */}
              <div style={{
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  background: colors.bgInput,
                  borderBottom: `1px solid ${colors.border}`
                }}>
                  <label style={{ color: colors.text, fontWeight: '600', margin: 0 }}>Line Items</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setShowProductDropdown(!showProductDropdown)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.4rem 0.75rem',
                          background: colors.info,
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.85rem',
                          cursor: 'pointer'
                        }}
                      >
                        <Plus size={14} /> Product
                      </button>
                      {showProductDropdown && products.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          background: colors.bgCard,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 10,
                          minWidth: '220px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}>
                          {products.map(sku => (
                            <div
                              key={sku.id}
                              onClick={() => { addProductToJob(sku); setShowProductDropdown(false); }}
                              style={{
                                padding: '0.75rem',
                                cursor: 'pointer',
                                borderBottom: `1px solid ${colors.border}`,
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = colors.bgInput}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              <span style={{ color: colors.text, fontSize: '0.9rem' }}>{sku.name}</span>
                              <span style={{ color: colors.success, fontWeight: '600' }}>
                                ${(parseFloat(sku.price || sku.base_price || sku.base_cost || 0)).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setShowAddJobItem(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.4rem 0.75rem',
                        background: colors.accent,
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}
                    >
                      <Plus size={14} /> Custom
                    </button>
                  </div>
                </div>

                {loadingJobItems ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: colors.textMuted }}>
                    Loading items...
                  </div>
                ) : editingJobItems.length === 0 ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: colors.textMuted }}>
                    No items yet. Add items to define job pricing.
                  </div>
                ) : (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {editingJobItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem 1rem',
                          borderBottom: `1px solid ${colors.border}`,
                          background: colors.bgCard
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ color: colors.text, fontWeight: '500' }}>{item.name}</div>
                          <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                            {item.quantity} × ${(item.unit_price || 0).toFixed(2)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ color: colors.text, fontWeight: '600' }}>
                            ${(item.total_price || 0).toFixed(2)}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeJobItem(item.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: colors.danger,
                              cursor: 'pointer',
                              padding: '0.25rem'
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Item Form */}
                {showAddJobItem && (
                  <div style={{
                    padding: '1rem',
                    background: colors.bgInput,
                    borderTop: `1px solid ${colors.border}`
                  }}>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        placeholder="Item name"
                        value={newJobItemName}
                        onChange={(e) => setNewJobItemName(e.target.value)}
                        style={{
                          flex: 2,
                          padding: '0.5rem',
                          background: colors.bgCard,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          fontSize: '0.9rem'
                        }}
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        value={newJobItemQty}
                        onChange={(e) => setNewJobItemQty(parseInt(e.target.value) || 1)}
                        style={{
                          width: '60px',
                          padding: '0.5rem',
                          background: colors.bgCard,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          fontSize: '0.9rem'
                        }}
                      />
                      <input
                        type="number"
                        placeholder="Price"
                        value={newJobItemPrice}
                        onChange={(e) => setNewJobItemPrice(e.target.value)}
                        style={{
                          width: '80px',
                          padding: '0.5rem',
                          background: colors.bgCard,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => {
                          setShowAddJobItem(false);
                          setNewJobItemName('');
                          setNewJobItemPrice('');
                          setNewJobItemQty(1);
                        }}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: colors.bgCard,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.textMuted,
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={addJobItem}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: colors.success,
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem 1rem',
                  background: colors.bgInput,
                  borderTop: `1px solid ${colors.border}`
                }}>
                  <span style={{ color: colors.text, fontWeight: '600' }}>Total</span>
                  <span style={{ color: colors.success, fontWeight: 'bold', fontSize: '1.1rem' }}>
                    ${(editingJobItems.reduce((sum, item) => sum + (item.total_price || 0), 0) || editingJob.quoted_value || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Notes</label>
                <textarea
                  value={editingJob.notes || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, notes: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Job Photos */}
              <div style={{
                padding: '1rem',
                background: colors.bgInput,
                borderRadius: '8px',
                border: `1px solid ${colors.border}`
              }}>
                <JobPhotos
                  jobId={editingJob.id}
                  companyId={companyId}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => {
                    setEditingJob(null);
                    setEditingJobItems([]);
                    setShowAddJobItem(false);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateJob(editingJob.id, {
                    status: editingJob.status,
                    job_type: editingJob.job_type,
                    scheduled_date: editingJob.scheduled_date,
                    scheduled_time: editingJob.scheduled_time,
                    quoted_value: editingJob.quoted_value,
                    notes: editingJob.notes
                  })}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.success,
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              {/* Activity Log */}
              <AuditLogViewer tableName="jobs" recordId={editingJob.id} />

              {/* Workflow Action Buttons */}
              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1.5rem',
                borderTop: `1px solid ${colors.border}`,
                display: 'flex',
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}>
                {editingJob.status !== 'completed' ? (
                  <>
                    <button
                      onClick={() => {
                        setEditingJob(null);
                        setEditingJobItems([]);
                        openCompletionModal(editingJob);
                      }}
                      style={{
                        flex: 1,
                        padding: '0.875rem',
                        background: colors.success,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        minWidth: '160px'
                      }}
                    >
                      <CheckCircle size={18} />
                      Complete & Invoice
                    </button>
                    <button
                      onClick={() => {
                        setEditingJob(null);
                        setEditingJobItems([]);
                        openScheduleModal(editingJob);
                      }}
                      style={{
                        padding: '0.875rem 1.25rem',
                        background: colors.info,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Calendar size={18} />
                      {editingJob.scheduled_date ? 'Reschedule' : 'Schedule'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setEditingJob(null);
                      setEditingJobItems([]);
                      viewJobInvoice(editingJob);
                    }}
                    disabled={loadingInvoice}
                    style={{
                      flex: 1,
                      padding: '0.875rem',
                      background: colors.primary,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      cursor: loadingInvoice ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <FileText size={18} />
                    {loadingInvoice ? 'Loading...' : 'View Invoice'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quote Modal */}
      {editingQuote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem',
          overflow: 'auto'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '2rem',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                Edit Quote: {editingQuote.quote_number}
              </h3>
              <button
                onClick={() => { setEditingQuote(null); setEditingQuoteItems([]); }}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Status</label>
                <select
                  value={editingQuote.status || ''}
                  onChange={(e) => setEditingQuote({ ...editingQuote, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="signed">Signed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Line Items Section */}
              <div style={{
                background: colors.bgInput,
                borderRadius: '8px',
                padding: '1rem',
                border: `1px solid ${colors.border}`
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <label style={{ color: colors.text, fontWeight: '600' }}>Line Items</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setShowQuoteProductDropdown(!showQuoteProductDropdown)}
                        style={{
                          padding: '0.4rem 0.75rem',
                          background: colors.info,
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <Plus size={14} /> Product
                      </button>
                      {showQuoteProductDropdown && products.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          background: colors.bgCard,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 10,
                          minWidth: '220px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}>
                          {products.map(sku => (
                            <div
                              key={sku.id}
                              onClick={() => { addProductToQuote(sku); setShowQuoteProductDropdown(false); }}
                              style={{
                                padding: '0.75rem',
                                cursor: 'pointer',
                                borderBottom: `1px solid ${colors.border}`,
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = colors.bgInput}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              <span style={{ color: colors.text, fontSize: '0.9rem' }}>{sku.name}</span>
                              <span style={{ color: colors.success, fontWeight: '600' }}>
                                ${(parseFloat(sku.price || sku.base_price || sku.base_cost || 0)).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setShowAddQuoteItem(!showAddQuoteItem)}
                      style={{
                        padding: '0.4rem 0.75rem',
                        background: colors.accent,
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      <Plus size={14} /> Custom
                    </button>
                  </div>
                </div>

                {loadingQuoteItems ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: colors.textMuted }}>
                    Loading items...
                  </div>
                ) : editingQuoteItems.length === 0 ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: colors.textMuted }}>
                    No items. Add products or custom items above.
                  </div>
                ) : (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {editingQuoteItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.5rem',
                          background: colors.bgCard,
                          borderRadius: '6px',
                          marginBottom: '0.5rem'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ color: colors.text, fontSize: '0.9rem' }}>{item.name}</div>
                          <div style={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                            {item.quantity} × ${(item.unit_price || 0).toFixed(2)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ color: colors.success, fontWeight: '600' }}>
                            ${(item.total_price || 0).toFixed(2)}
                          </span>
                          <button
                            onClick={() => removeQuoteItem(item.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: colors.danger,
                              cursor: 'pointer',
                              padding: '0.25rem'
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Custom Item Form */}
                {showAddQuoteItem && (
                  <div style={{
                    padding: '0.75rem',
                    background: colors.bgCard,
                    borderRadius: '6px',
                    marginTop: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        placeholder="Item name"
                        value={newQuoteItemName}
                        onChange={(e) => setNewQuoteItemName(e.target.value)}
                        style={{
                          flex: 2,
                          padding: '0.5rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          fontSize: '0.9rem'
                        }}
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        value={newQuoteItemQty}
                        onChange={(e) => setNewQuoteItemQty(parseInt(e.target.value) || 1)}
                        style={{
                          width: '60px',
                          padding: '0.5rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          fontSize: '0.9rem',
                          textAlign: 'center'
                        }}
                      />
                      <input
                        type="number"
                        placeholder="Price"
                        value={newQuoteItemPrice}
                        onChange={(e) => setNewQuoteItemPrice(e.target.value)}
                        style={{
                          width: '80px',
                          padding: '0.5rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          fontSize: '0.9rem',
                          textAlign: 'right'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setShowAddQuoteItem(false)}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.textMuted,
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={addQuoteItem}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: colors.success,
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem 0.5rem 0',
                  marginTop: '0.5rem',
                  borderTop: `1px solid ${colors.border}`
                }}>
                  <span style={{ color: colors.text, fontWeight: '600' }}>Total</span>
                  <span style={{ color: colors.success, fontWeight: 'bold', fontSize: '1.1rem' }}>
                    ${(() => {
                      const sub = editingQuoteItems.reduce((s, it) => s + (it.total_price || 0), 0);
                      if (!sub) return (editingQuote.total || 0).toFixed(2);
                      const afterDisc = sub - (editingQuote.discount || 0);
                      return (afterDisc + afterDisc * (editingQuote.tax_rate || 0)).toFixed(2);
                    })()}
                  </span>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Notes</label>
                <textarea
                  value={editingQuote.notes || ''}
                  onChange={(e) => setEditingQuote({ ...editingQuote, notes: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => { setEditingQuote(null); setEditingQuoteItems([]); }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Recompute the full money stack so an item edit doesn't drop
                    // the quote's discount + tax (previously overwrote total with a
                    // raw line sum), and keep subtotal/tax_amount/total consistent.
                    const sub = editingQuoteItems.reduce((s, it) => s + (it.total_price || 0), 0)
                      || editingQuote.subtotal || editingQuote.total || 0;
                    const afterDisc = sub - (editingQuote.discount || 0);
                    const taxAmt = afterDisc * (editingQuote.tax_rate || 0);
                    handleUpdateQuote(editingQuote.id, {
                      status: editingQuote.status,
                      subtotal: sub,
                      tax_amount: taxAmt,
                      total: afterDisc + taxAmt,
                      notes: editingQuote.notes
                    });
                  }}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.success,
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              {/* Activity Log */}
              <AuditLogViewer tableName="quotes" recordId={editingQuote.id} />

              {/* Workflow Action Buttons */}
              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1.5rem',
                borderTop: `1px solid ${colors.border}`,
                display: 'flex',
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}>
                {/* Send Quote Button */}
                <button
                  onClick={() => {
                    const quoteData = editingQuote;
                    setEditingQuote(null);
                    setEditingQuoteItems([]);
                    openSendQuoteModal(quoteData);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: colors.accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Send size={18} />
                  Send Quote
                </button>

                {/* Schedule Job Button (for signed quotes) */}
                {editingQuote.status === 'signed' && (
                  <button
                    onClick={() => {
                      const quoteData = editingQuote;
                      setEditingQuote(null);
                      setEditingQuoteItems([]);
                      setQuoteToCreateJob(quoteData);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.875rem',
                      background: colors.info,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Calendar size={18} />
                    Schedule Job
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Completion Modal */}
      {showCompletionModal && jobToComplete && (
        <JobCompletionModal
          job={jobToComplete}
          companyId={companyId}
          onClose={() => {
            setShowCompletionModal(false);
            setJobToComplete(null);
            loadData(); // Refresh jobs list — job status may have changed via RPC
          }}
          onComplete={handleJobCompleted}
          onNavigate={onNavigate}
        />
      )}

      {/* Job Planner Modal */}
      {showPlannerModal && jobToPlan && (
        <JobPlannerModal
          job={jobToPlan}
          companyId={companyId}
          onClose={() => {
            setShowPlannerModal(false);
            setJobToPlan(null);
          }}
          onSave={() => loadData()}
        />
      )}

      {/* Invoice View Modal */}
      {showInvoiceModal && viewingInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem',
          overflow: 'auto'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={() => {
                setShowInvoiceModal(false);
                setViewingInvoice(null);
                setInvoiceItems([]);
              }}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#f0f0f0',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              <X size={18} color="#666" />
            </button>

            {/* Invoice Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '2px solid #e0e0e0'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <div>
                  <h2 style={{
                    margin: 0,
                    color: '#333',
                    fontSize: '1.25rem',
                    fontWeight: '700'
                  }}>
                    {companyInfo?.name || 'Company'}
                  </h2>
                  {companyInfo?.phone && (
                    <div style={{ color: '#666', fontSize: '0.85rem' }}>{companyInfo.phone}</div>
                  )}
                  {companyInfo?.email && (
                    <div style={{ color: '#666', fontSize: '0.85rem' }}>{companyInfo.email}</div>
                  )}
                </div>
                <div>
                  <h1 style={{
                    margin: 0,
                    color: colors.accent,
                    fontSize: '1.5rem',
                    fontWeight: '700'
                  }}>
                    INVOICE
                  </h1>
                  <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    <div><strong>Invoice #:</strong> {viewingInvoice.invoice_number}</div>
                    <div><strong>Date:</strong> {formatDisplayDate(viewingInvoice.created_at?.split('T')[0])}</div>
                    <div><strong>Due:</strong> {formatDisplayDate(viewingInvoice.due_date)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bill To */}
            <div style={{ padding: '1rem 1.5rem' }}>
              <h3 style={{
                margin: '0 0 0.25rem 0',
                color: '#333',
                fontSize: '0.8rem',
                fontWeight: '700',
                textTransform: 'uppercase'
              }}>
                Bill To
              </h3>
              <div style={{ color: '#333', fontSize: '0.95rem', fontWeight: '600' }}>
                {viewingInvoice.customerName}
              </div>
              {viewingInvoice.customers?.phone && (
                <div style={{ color: '#666', fontSize: '0.85rem' }}>{viewingInvoice.customers.phone}</div>
              )}
              {viewingInvoice.customers?.email && (
                <div style={{ color: '#666', fontSize: '0.85rem' }}>{viewingInvoice.customers.email}</div>
              )}
            </div>

            {/* Line Items */}
            <div style={{ padding: '0 1.5rem', overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.85rem',
                minWidth: '300px'
              }}>
                <thead>
                  <tr style={{ background: '#333' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: '#fff', fontWeight: '600' }}>Description</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#fff', fontWeight: '600', width: '50px' }}>Qty</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', color: '#fff', fontWeight: '600', width: '70px' }}>Price</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', color: '#fff', fontWeight: '600', width: '70px' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceItems.length > 0 ? (
                    invoiceItems.map((item, idx) => (
                      <tr key={item.id || idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={{ padding: '0.5rem', color: '#333' }}>{item.description || item.name || 'Item'}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'center', color: '#333' }}>{item.quantity || 1}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: '#333' }}>${(item.unit_price || item.price || 0).toFixed(2)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: '#333', fontWeight: '600' }}>${((item.quantity || 1) * (item.unit_price || item.price || 0)).toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '0.5rem', color: '#333' }}>Services rendered</td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', color: '#333' }}>1</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', color: '#333' }}>${(viewingInvoice.subtotal || viewingInvoice.total || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', color: '#333', fontWeight: '600' }}>${(viewingInvoice.subtotal || viewingInvoice.total || 0).toFixed(2)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div style={{ padding: '1rem 1.5rem' }}>
              <div style={{ maxWidth: '200px', marginLeft: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid #e0e0e0' }}>
                  <span style={{ color: '#666', fontSize: '0.85rem' }}>Subtotal</span>
                  <span style={{ color: '#333', fontSize: '0.85rem' }}>${(viewingInvoice.subtotal || viewingInvoice.total || 0).toFixed(2)}</span>
                </div>
                {(viewingInvoice.tax_amount || viewingInvoice.tax) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid #e0e0e0' }}>
                    <span style={{ color: '#666', fontSize: '0.85rem' }}>Tax</span>
                    <span style={{ color: '#333', fontSize: '0.85rem' }}>${(viewingInvoice.tax_amount || viewingInvoice.tax || 0).toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '2px solid #333' }}>
                  <span style={{ color: '#333', fontWeight: '700' }}>Total</span>
                  <span style={{ color: '#333', fontWeight: '700' }}>${(viewingInvoice.total || 0).toFixed(2)}</span>
                </div>
                {viewingInvoice.amount_paid > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0' }}>
                    <span style={{ color: '#22c55e', fontSize: '0.85rem' }}>Paid</span>
                    <span style={{ color: '#22c55e', fontSize: '0.85rem' }}>-${(viewingInvoice.amount_paid || 0).toFixed(2)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  background: viewingInvoice.balance > 0 ? '#fef3c7' : '#dcfce7',
                  borderRadius: '6px',
                  marginTop: '0.5rem'
                }}>
                  <span style={{ color: viewingInvoice.balance > 0 ? '#92400e' : '#166534', fontWeight: '700', fontSize: '0.9rem' }}>Balance Due</span>
                  <span style={{ color: viewingInvoice.balance > 0 ? '#92400e' : '#166534', fontWeight: '700', fontSize: '0.9rem' }}>${(viewingInvoice.balance || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid #e0e0e0',
              textAlign: 'center',
              color: '#999',
              fontSize: '0.8rem'
            }}>
              Thank you for your business!
            </div>

            {/* Action Buttons */}
            <div style={{
              padding: '1rem 1.5rem 1.5rem',
              display: 'flex',
              gap: '0.5rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={printInvoice}
                style={{
                  padding: '0.6rem 1rem',
                  background: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
              >
                <Printer size={14} />
                Print
              </button>
              <button
                onClick={downloadInvoicePDF}
                style={{
                  padding: '0.6rem 1rem',
                  background: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
              >
                <Download size={14} />
                Download
              </button>
              <button
                onClick={copyPaymentLink}
                style={{
                  padding: '0.6rem 1rem',
                  background: colors.accent,
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
              >
                <Link size={14} />
                Payment Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Communications Modal */}
      {showCommunicationsModal && selectedCustomerForComms && (
        <CustomerCommunications
          customer={selectedCustomerForComms}
          companyId={companyId}
          onClose={() => {
            setShowCommunicationsModal(false);
            setSelectedCustomerForComms(null);
          }}
        />
      )}

      {/* Send Quote Modal */}
      {showSendQuoteModal && quoteToSend && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: isMobile ? '1.5rem' : '2rem',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            border: `2px solid ${colors.accent}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: `${colors.accent}30`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Send size={20} style={{ color: colors.accent }} />
              </div>
              <div>
                <h3 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
                  Send Quote to Customer
                </h3>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  {quoteToSend.quote_number} - ${quoteToSend.total?.toFixed(2)}
                </div>
              </div>
              <button
                onClick={() => setShowSendQuoteModal(false)}
                style={{
                  marginLeft: 'auto',
                  background: 'transparent',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Customer Info - Editable */}
            {quoteToSend.customers && (
              <div style={{
                background: colors.bgInput,
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div style={{ color: colors.text, fontWeight: 'bold' }}>
                    {quoteToSend.customers.name}
                  </div>
                  <button
                    onClick={() => setEditingSendContactInfo(!editingSendContactInfo)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: colors.accent,
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Edit3 size={14} />
                    {editingSendContactInfo ? 'Done' : 'Edit'}
                  </button>
                </div>

                {editingSendContactInfo ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                        Email
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Mail size={14} style={{ color: colors.textMuted }} />
                        <input
                          type="email"
                          value={sendQuoteEmail}
                          onChange={(e) => setSendQuoteEmail(e.target.value)}
                          placeholder="customer@email.com"
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            background: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                        Phone
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <MessageSquare size={14} style={{ color: colors.textMuted }} />
                        <input
                          type="tel"
                          value={sendQuotePhone}
                          onChange={(e) => setSendQuotePhone(e.target.value)}
                          placeholder="555-123-4567"
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            background: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ color: colors.textMuted, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Mail size={14} /> {sendQuoteEmail || <span style={{ fontStyle: 'italic' }}>No email</span>}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                      <MessageSquare size={14} /> {sendQuotePhone || <span style={{ fontStyle: 'italic' }}>No phone</span>}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Additional Recipients */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Additional Recipients
                </label>
                <button
                  onClick={() => setSendQuoteAdditionalRecipients([...sendQuoteAdditionalRecipients, { email: '', phone: '' }])}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    padding: '0.35rem 0.75rem',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <UserPlus size={14} />
                  Add
                </button>
              </div>

              {sendQuoteAdditionalRecipients.length === 0 ? (
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', fontStyle: 'italic' }}>
                  No additional recipients
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {sendQuoteAdditionalRecipients.map((recipient, idx) => (
                    <div key={idx} style={{
                      background: colors.bgInput,
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>Recipient {idx + 2}</span>
                        <button
                          onClick={() => setSendQuoteAdditionalRecipients(sendQuoteAdditionalRecipients.filter((_, i) => i !== idx))}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#e74c3c',
                            cursor: 'pointer',
                            padding: '0.25rem'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 150px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Mail size={12} style={{ color: colors.textMuted }} />
                            <input
                              type="email"
                              value={recipient.email}
                              onChange={(e) => {
                                const updated = [...sendQuoteAdditionalRecipients];
                                updated[idx].email = e.target.value;
                                setSendQuoteAdditionalRecipients(updated);
                              }}
                              placeholder="Email"
                              style={{
                                flex: 1,
                                padding: '0.4rem',
                                background: colors.bgCard,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '4px',
                                color: colors.text,
                                fontSize: '0.85rem'
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ flex: '1 1 150px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <MessageSquare size={12} style={{ color: colors.textMuted }} />
                            <input
                              type="tel"
                              value={recipient.phone}
                              onChange={(e) => {
                                const updated = [...sendQuoteAdditionalRecipients];
                                updated[idx].phone = e.target.value;
                                setSendQuoteAdditionalRecipients(updated);
                              }}
                              placeholder="Phone"
                              style={{
                                flex: 1,
                                padding: '0.4rem',
                                background: colors.bgCard,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '4px',
                                color: colors.text,
                                fontSize: '0.85rem'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quote Link */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.text,
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}>
                Quote Link
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  readOnly
                  value={getQuoteUrl(quoteToSend.id)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    fontSize: '0.85rem'
                  }}
                />
                <button
                  onClick={copyQuoteLink}
                  style={{
                    padding: '0.75rem 1rem',
                    background: quoteLinkCopied ? '#22c55e' : colors.bgInput,
                    border: `1px solid ${quoteLinkCopied ? '#22c55e' : colors.border}`,
                    borderRadius: '8px',
                    color: quoteLinkCopied ? '#fff' : colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {quoteLinkCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
                  {quoteLinkCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Send Method */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.75rem',
                color: colors.text,
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}>
                Send via
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[
                  { value: 'email', label: 'Email', icon: Mail },
                  { value: 'sms', label: 'SMS', icon: MessageSquare },
                  { value: 'both', label: 'Both', icon: Send }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setSendQuoteMethod(option.value)}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: sendQuoteMethod === option.value ? `${colors.accent}20` : colors.bgInput,
                      border: `2px solid ${sendQuoteMethod === option.value ? colors.accent : colors.border}`,
                      borderRadius: '8px',
                      color: sendQuoteMethod === option.value ? colors.accent : colors.text,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      fontWeight: sendQuoteMethod === option.value ? 'bold' : 'normal',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <option.icon size={16} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowSendQuoteModal(false)}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.border,
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minHeight: '48px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendQuote}
                disabled={sendingQuote}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: sendingQuote ? colors.border : colors.accent,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: sendingQuote ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  minHeight: '48px',
                  opacity: sendingQuote ? 0.7 : 1
                }}
              >
                {sendingQuote ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send size={18} />
                    Send Quote
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
